'use client';

/**
 * AI visibility guide hero: a borrower types the question into a chat box and sends it, the AI "thinks", streams an answer
 * (drawn as word bars, never invented words) and lists the sources it cited, with "Your website" last and lit.
 * Plays once when the page opens (and the tab is visible), then holds the finished state.
 * Every element sits in its final place from the first paint and is only faded or moved in, so nothing below it shifts.
 * The full question is in the server HTML (the sent bubble); the chat box shows a typed slice of it.
 * Reduced motion, or no JavaScript: the finished state, by CSS alone (see guide-art-css.ts, .ga-chat).
 * The question is the study's own prompt (docs/research/chatgpt-prompts-v2-local.json) with the city left as a placeholder.
 */
import React, { useEffect, useState } from 'react';

const QUESTION = 'I am buying a home in [your city, ST]. Who should I use for a mortgage?';
const SOURCES = ['Review site', 'Directory', 'Ranking site'];
// drawn word widths per answer line (percent of the line), so the answer reads as text being written, not as a progress bar
const LINES: number[][] = [[14, 22, 9, 18, 12, 15], [19, 11, 24, 16, 8], [12, 17, 21, 10, 19, 9]];

// phases in order; each one adds a class to the card
const PHASES = ['typing', 'sent', 'think', 'stream', 'src', 'done'] as const;
type Phase = (typeof PHASES)[number] | 'start';

const TYPE_MS = 34; // per character, with longer pauses after punctuation
const NO_JS = '<style>.ga-chat .ga-ai-q,.ga-chat .ga-ai-a,.ga-chat .ga-ai-w,.ga-chat .ga-ai-src,.ga-chat .ga-ai-chips span{opacity:1!important;transform:none!important}.ga-chat .ga-ai-caret{opacity:0!important;animation:none!important}</style>';

export function GuideAiHero() {
  const [phase, setPhase] = useState<Phase>('start');
  const [typed, setTyped] = useState(0);
  const [press, setPress] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setTyped(0); setPhase('done'); return; }
    const timers: number[] = [];
    const at = (ms: number, fn: () => void) => { timers.push(window.setTimeout(fn, ms)); };
    let started = false;
    const run = () => {
      if (started) return; started = true;
      let t = 450;
      at(t, () => setPhase('typing'));
      for (let i = 1; i <= QUESTION.length; i++) {
        const prev = QUESTION[i - 2];
        t += TYPE_MS + (prev === '.' || prev === '?' ? 220 : prev === ',' ? 120 : 0);
        at(t, () => setTyped(i));
      }
      t += 420; at(t, () => setPress(true));
      t += 160; at(t, () => { setPress(false); setTyped(0); setPhase('sent'); });
      t += 520; at(t, () => setPhase('think'));
      t += 800; at(t, () => setPhase('stream'));
      t += 1350; at(t, () => setPhase('src'));
      t += 1100; at(t, () => setPhase('done'));
    };
    // a tab opened in the background waits until it is shown
    const onVis = () => { if (document.visibilityState === 'visible') run(); };
    if (document.visibilityState === 'visible') run(); else document.addEventListener('visibilitychange', onVis);
    return () => { timers.forEach(clearTimeout); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  const idx = phase === 'start' ? -1 : PHASES.indexOf(phase);
  const cls = ['ga', 'ga-ai', 'ga-chat', 'is-run'];
  if (idx >= 0) cls.push('is-typing');
  if (idx >= 1) cls.push('is-sent');
  if (idx >= 2) cls.push('is-think');
  if (idx >= 3) cls.push('is-stream');
  if (idx >= 4) cls.push('is-src');
  if (idx >= 5) cls.push('is-done');
  let k = 0; // running word index, for the streaming delay

  return (
    <div className={cls.join(' ')} aria-hidden="true">
      {/* without JavaScript the chat never plays, so show it finished */}
      <noscript dangerouslySetInnerHTML={{ __html: NO_JS }} />
      <span className="ga-ai-empty"><i /><span>AI assistant</span></span>
      <div className="ga-ai-q">
        <span className="ga-ai-who">Borrower asks</span>
        <p>{QUESTION}</p>
      </div>
      <div className="ga-ai-a">
        <span className="ga-ai-who ga-ai-bot"><i />AI answer<span className="ga-ai-dots"><b /><b /><b /></span></span>
        {LINES.map((ws, li) => (
          <span className="ga-ai-ln" key={li}>
            {ws.map((w, wi) => <span className="ga-ai-w" key={wi} style={{ width: `${w}%`, ['--k' as string]: k++ } as React.CSSProperties} />)}
          </span>
        ))}
        <span className="ga-ai-src">Sources cited</span>
        <span className="ga-ai-chips">
          {SOURCES.map((t, i) => <span key={t} style={{ ['--i' as string]: i } as React.CSSProperties}>{t}</span>)}
          <span className="is-you" style={{ ['--i' as string]: SOURCES.length } as React.CSSProperties}>Your website</span>
        </span>
      </div>
      <div className="ga-ai-box">
        <span className="ga-ai-field">
          {typed > 0 ? <span className="ga-ai-typed">{QUESTION.slice(0, typed)}</span> : <span className="ga-ai-ph">Ask anything</span>}
          <span className="ga-ai-caret" />
        </span>
        <span className={press ? 'ga-ai-send is-press' : 'ga-ai-send'}>
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M10 15.5V4.5M5 9.5l5-5 5 5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </span>
      </div>
    </div>
  );
}
